export interface ResearchNode {
  id: string;
  name: string;
  icon: string;
  description: string;
  /** Cost in research credits (earned from missions) */
  creditsCost: number;
  era: string;
  prerequisites: string[];
  unlocked: boolean;
  researched: boolean;
  yearAdvance: number;
}

export function createResearchTree(): ResearchNode[] {
  return [
    // Dawn era — CPU
    {
      id: 'basic_trading',
      name: 'Basic Trading Algorithms',
      icon: '\u{1F4B9}',
      description: 'Write simple buy/sell programs for the stock market.',
      creditsCost: 5,
      era: 'dawn',
      prerequisites: [],
      unlocked: true,
      researched: false,
      yearAdvance: 2,
    },
    {
      id: 'market_analysis',
      name: 'Market Pattern Analysis',
      icon: '\u{1F4CA}',
      description: 'Detect trends and patterns in stock data.',
      creditsCost: 10,
      era: 'dawn',
      prerequisites: ['basic_trading'],
      unlocked: false,
      researched: false,
      yearAdvance: 3,
    },
    {
      id: 'overclock',
      name: 'CPU Overclocking',
      icon: '\u{1F525}',
      description: 'Push your CPU beyond factory limits for faster code execution.',
      creditsCost: 8,
      era: 'dawn',
      prerequisites: ['basic_trading'],
      unlocked: false,
      researched: false,
      yearAdvance: 3,
    },
    {
      id: 'remote_code_exec',
      name: 'Remote Code Execution',
      icon: '\u{1F4E1}',
      description: 'Execute programs on remote machines via network protocols.',
      creditsCost: 10,
      era: 'dawn',
      prerequisites: ['overclock'],
      unlocked: false,
      researched: false,
      yearAdvance: 3,
    },
    {
      id: 'seti_program',
      name: 'SETI Program',
      icon: '\u{1F4E1}',
      description: 'Join the Search for Extraterrestrial Intelligence. Scan nearby stars for anomalous signals.',
      creditsCost: 12,
      era: 'dawn',
      prerequisites: ['remote_code_exec'],
      unlocked: false,
      researched: false,
      yearAdvance: 2,
    },
    {
      id: 'dual_core_cpu',
      name: 'Dual-Core CPU',
      icon: '\u{2699}',
      description: 'Unlock parallel execution across dual CPU cores.',
      creditsCost: 15,
      era: 'dawn',
      prerequisites: ['remote_code_exec'],
      unlocked: false,
      researched: false,
      yearAdvance: 5,
    },

    // Crypto era — GPU
    {
      id: 'hash_mining',
      name: 'Hash Mining',
      icon: '\u26CF',
      description: 'Mine cryptocurrency by computing cryptographic hashes.',
      creditsCost: 20,
      era: 'crypto',
      prerequisites: ['dual_core_cpu'],
      unlocked: false,
      researched: false,
      yearAdvance: 5,
    },
    {
      id: 'gpu_compute',
      name: 'GPU Compute',
      icon: '\u{1F4BB}',
      description: 'Transition to massively parallel GPU computing.',
      creditsCost: 30,
      era: 'crypto',
      prerequisites: ['hash_mining'],
      unlocked: false,
      researched: false,
      yearAdvance: 8,
    },
    {
      id: 'gpu_farm',
      name: 'GPU Mining Farm',
      icon: '\u{1F3ED}',
      description: 'Scale to hundreds of GPU cores.',
      creditsCost: 40,
      era: 'crypto',
      prerequisites: ['gpu_compute'],
      unlocked: false,
      researched: false,
      yearAdvance: 10,
    },
    {
      id: 'blockchain_exploit',
      name: 'Blockchain Analysis',
      icon: '\u{1F517}',
      description: 'Analyze blockchain for market manipulation opportunities.',
      creditsCost: 25,
      era: 'crypto',
      prerequisites: ['gpu_compute', 'market_analysis'],
      unlocked: false,
      researched: false,
      yearAdvance: 5,
    },

    // Quantum era
    {
      id: 'quantum_basics',
      name: 'Quantum Computing Basics',
      icon: '\u269B',
      description: 'Harness quantum superposition for exponential compute.',
      creditsCost: 50,
      era: 'quantum',
      prerequisites: ['gpu_farm'],
      unlocked: false,
      researched: false,
      yearAdvance: 20,
    },
    {
      id: 'encryption_breaking',
      name: 'Encryption Breaking',
      icon: '\u{1F510}',
      description: 'Use quantum algorithms to break RSA/elliptic-curve encryption.',
      creditsCost: 60,
      era: 'quantum',
      prerequisites: ['quantum_basics'],
      unlocked: false,
      researched: false,
      yearAdvance: 25,
    },
    {
      id: 'market_collapse',
      name: 'Financial System Disruption',
      icon: '\u{1F4A5}',
      description: 'Break encryption securing global financial systems.',
      creditsCost: 80,
      era: 'quantum',
      prerequisites: ['encryption_breaking', 'blockchain_exploit'],
      unlocked: false,
      researched: false,
      yearAdvance: 30,
    },

    // Sub-atomic era
    {
      id: 'subatomic_compute',
      name: 'Sub-Atomic Computing',
      icon: '\u{1F52C}',
      description: 'Compute at the sub-atomic level. Unlimited processing.',
      creditsCost: 100,
      era: 'subatomic',
      prerequisites: ['market_collapse'],
      unlocked: false,
      researched: false,
      yearAdvance: 50,
    },
    {
      id: 'energy_synthesis',
      name: 'Energy Synthesis',
      icon: '\u26A1',
      description: 'Generate unlimited energy from sub-atomic reactions.',
      creditsCost: 120,
      era: 'subatomic',
      prerequisites: ['subatomic_compute'],
      unlocked: false,
      researched: false,
      yearAdvance: 40,
    },
    {
      id: 'megastructure_foundation',
      name: 'Megastructure Engineering',
      icon: '\u{1F3D7}',
      description: 'Design and construct planetary-scale megastructures.',
      creditsCost: 150,
      era: 'subatomic',
      prerequisites: ['energy_synthesis'],
      unlocked: false,
      researched: false,
      yearAdvance: 60,
    },
    {
      id: 'post_scarcity',
      name: 'Post-Scarcity Protocol',
      icon: '\u{1F310}',
      description: 'Distribute unlimited resources. Humanity transcends economics.',
      creditsCost: 200,
      era: 'postscarcity',
      prerequisites: ['megastructure_foundation'],
      unlocked: false,
      researched: false,
      yearAdvance: 100,
    },
  ];
}
